// client/src/components/Supplier.js
import React, { useEffect, useMemo, useState, useRef } from 'react';
import axios from 'axios';
import {
  Row, Col, Card, Input, Select, Button, Modal, Form, Upload, Space, Tag, Pagination, Divider, Popconfirm,
  Typography, Tooltip, Image, Descriptions
} from 'antd';
import { PlusOutlined, UploadOutlined, DownloadOutlined, DeleteOutlined, EditOutlined, EyeOutlined } from '@ant-design/icons';
import '../styles/supplier.css';

const { Option } = Select;
const { Title, Text } = Typography;
const API_BASE_URL = process.env.REACT_APP_API_BASE_URL || '';

function isImg(m) { return m && m.startsWith('image/'); }
// number of images for an item (covers new images[] array and legacy single image)
function imageCountOf(it) {
  if (typeof it.imageCount === 'number') return it.imageCount;
  return isImg(it.mimeType) ? 1 : 0;
}
// URL for a given image index of an item (version busts the cache after edits)
function imageUrl(id, index = 0, inline = true, version = '') {
  const v = version ? `&v=${encodeURIComponent(version)}` : '';
  return `${API_BASE_URL}/api/suppliers/file/${id}?index=${index}${inline ? '&inline=1' : ''}${v}`;
}
const useDebounce = (fn, delay = 300) => {
  const t = useRef();
  return (...args) => {
    clearTimeout(t.current);
    t.current = setTimeout(() => fn(...args), delay);
  };
};

const Supplier = () => {
  const [brands, setBrands] = useState([]);
  const [brandSearch, setBrandSearch] = useState('');
  const [brandId, setBrandId] = useState(undefined);

  const [q, setQ] = useState('');
  const [tags, setTags] = useState([]);
  const [cats, setCats] = useState([]);

  const [tagOptions, setTagOptions] = useState([]);
  const [catOptions, setCatOptions] = useState([]);

  const [items, setItems] = useState([]);
  const [total, setTotal] = useState(0);
  const [page, setPage]   = useState(1);
  const [limit, setLimit] = useState(24);
  const [loading, setLoading] = useState(false);

  const [brandModalOpen, setBrandModalOpen] = useState(false);
  const [brandForm] = Form.useForm();

  const [uploadOpen, setUploadOpen] = useState(false);
  const [uploadForm] = Form.useForm();
  const [fileList, setFileList] = useState([]);
  const [uploading, setUploading] = useState(false);

  const [editOpen, setEditOpen] = useState(false);
  const [editingItem, setEditingItem] = useState(null);
  const [editForm] = Form.useForm();
  const [editImageCount, setEditImageCount] = useState(0);
  const [editRemove, setEditRemove] = useState([]); // indices marked for removal
  const [editNewFiles, setEditNewFiles] = useState([]); // new images to add
  const [savingEdit, setSavingEdit] = useState(false);

  const [detailOpen, setDetailOpen] = useState(false);
  const [detailItem, setDetailItem] = useState(null);

  // ---------- load brands ----------
  const loadBrands = async () => {
    try {
      const res = await axios.get(`${API_BASE_URL}/api/suppliers/brands?search=${encodeURIComponent(brandSearch)}`, { withCredentials: true });
      setBrands(res.data || []);
    } catch {
      setBrands([]);
    }
  };
  useEffect(() => { loadBrands(); }, [brandSearch]);

  // ---------- load tags/cats (for suggestions) ----------
  const fetchTags = async (query = '') => {
    try {
      const res = await axios.get(`${API_BASE_URL}/api/suppliers/tags?search=${encodeURIComponent(query)}`, { withCredentials: true });
      setTagOptions((res.data || []).map(x => x.value));
    } catch { setTagOptions([]); }
  };
  const fetchCats = async (query = '') => {
    try {
      const res = await axios.get(`${API_BASE_URL}/api/suppliers/categories?search=${encodeURIComponent(query)}`, { withCredentials: true });
      setCatOptions((res.data || []).map(x => x.value));
    } catch { setCatOptions([]); }
  };
  const debouncedFetchTags = useDebounce(fetchTags, 250);
  const debouncedFetchCats = useDebounce(fetchCats, 250);

  // initial suggestions (top/popular)
  useEffect(() => { fetchTags(''); fetchCats(''); }, []);

  // ---------- load items ----------
  const loadItems = async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams({
        page: String(page),
        limit: String(limit),
      });
      if (q) params.set('q', q);
      if (brandId) params.set('brandId', brandId);
      if (tags.length) params.set('tags', tags.join(','));
      if (cats.length) params.set('categories', cats.join(','));

      const res = await axios.get(`${API_BASE_URL}/api/suppliers/items?${params.toString()}`, { withCredentials: true });
      const { data, total } = res.data || {};
      setItems(data || []);
      setTotal(total || 0);
    } catch {
      setItems([]); setTotal(0);
    } finally {
      setLoading(false);
    }
  };
  useEffect(() => { loadItems(); }, [q, brandId, tags, cats, page, limit]);

  // ---------- brand modal ----------
  const createBrand = async () => {
    try {
      const v = await brandForm.validateFields();
      const res = await axios.post(`${API_BASE_URL}/api/suppliers/brands`, v, { withCredentials: true });
      setBrandModalOpen(false);
      setBrandSearch(''); // refresh list
      await loadBrands();
      setBrandId(res.data?._id);
    } catch (e) {
      if (e?.errorFields) return;
      Modal.error({ title: 'Create Brand Failed', content: e?.response?.data?.error || 'Unknown error' });
    }
  };

  // ---------- upload ----------
  const beforeUpload = (file) => {
    const allowedDocs = [
      'application/pdf',
      'application/msword',
      'application/vnd.openxmlformats-officedocument.wordprocessingml.document',
      'application/vnd.ms-excel',
      'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
      'text/plain'
    ];
    if (!isImg(file.type) && !allowedDocs.includes(file.type)) {
      Modal.error({ title: 'Unsupported file', content: 'Only images and documents are allowed.' });
      return Upload.LIST_IGNORE;
    }
    return true;
  };
  const doUpload = async () => {
    try {
      if (!fileList.length) {
        Modal.error({ title: 'No file selected', content: 'Please select one or more images, or a document.' });
        return;
      }
      const v = await uploadForm.validateFields();

      const form = new FormData();
      fileList.forEach(f => form.append('files', f.originFileObj || f));
      if (v.brandId) form.append('brandId', v.brandId);
      else if (v.brandName) form.append('brandName', v.brandName);
      if (v.name) form.append('name', v.name);
      if (v.tags?.length) form.append('tags', v.tags.join(','));
      if (v.categories?.length) form.append('categories', v.categories.join(','));

      setUploading(true);
      await axios.post(`${API_BASE_URL}/api/suppliers/upload`, form, {
        withCredentials: true,
        headers: { 'Content-Type': 'multipart/form-data' },
      });

      setUploadOpen(false);
      uploadForm.resetFields();
      setFileList([]);
      loadItems();
      fetchTags();
      fetchCats();
      Modal.success({ title: 'Uploaded' });
    } catch (e) {
      if (e?.errorFields) return;
      Modal.error({ title: 'Upload failed', content: e?.response?.data?.error || 'Unknown error' });
    } finally {
      setUploading(false);
    }
  };

  // ---------- detail ----------
  const openDetail = (it) => {
    setDetailItem(it);
    setDetailOpen(true);
  };

  // ---------- edit ----------
  const openEdit = (it) => {
    setEditingItem(it);
    editForm.setFieldsValue({
      name: it.name,
      tags: it.tags,
      categories: it.categories,
    });
    setEditImageCount(imageCountOf(it));
    setEditRemove([]);
    setEditNewFiles([]);
    // preload suggestions to help user
    fetchTags('');
    fetchCats('');
    setEditOpen(true);
  };
  const toggleRemove = (index) => {
    setEditRemove(prev => prev.includes(index) ? prev.filter(i => i !== index) : [...prev, index]);
  };
  const saveEdit = async () => {
    try {
      const v = await editForm.validateFields();
      const id = editingItem._id;
      const isImageItem = editImageCount > 0;

      // Guard: an image item must keep at least one image
      if (isImageItem) {
        const remaining = editImageCount - editRemove.length + editNewFiles.length;
        if (remaining <= 0) {
          Modal.error({ title: 'Cannot remove all images', content: 'An item must have at least one image. Add a new image before removing the last one.' });
          return;
        }
      }

      setSavingEdit(true);

      // 1) add new images (indices of existing images are unaffected — appended at the end)
      if (editNewFiles.length) {
        const form = new FormData();
        editNewFiles.forEach(f => form.append('files', f.originFileObj || f));
        await axios.post(`${API_BASE_URL}/api/suppliers/items/${id}/images`, form, {
          withCredentials: true,
          headers: { 'Content-Type': 'multipart/form-data' },
        });
      }

      // 2) remove marked images (by original index)
      if (editRemove.length) {
        await axios.delete(`${API_BASE_URL}/api/suppliers/items/${id}/images`, {
          withCredentials: true,
          data: { indices: editRemove },
        });
      }

      // 3) save metadata
      await axios.put(`${API_BASE_URL}/api/suppliers/items/${id}`, v, { withCredentials: true });

      setEditOpen(false);
      setEditingItem(null);
      setEditRemove([]);
      setEditNewFiles([]);
      loadItems();
    } catch (e) {
      if (e?.errorFields) return;
      Modal.error({ title: 'Update failed', content: e?.response?.data?.error || 'Unknown error' });
    } finally {
      setSavingEdit(false);
    }
  };
  const deleteItem = async (it) => {
    try {
      await axios.delete(`${API_BASE_URL}/api/suppliers/items/${it._id}`, { withCredentials: true });
      loadItems();
    } catch (e) {
      Modal.error({ title: 'Delete failed', content: e?.response?.data?.error || 'Unknown error' });
    }
  };

  // ---------- UI ----------
  const BrandSelect = (
    <Select
      allowClear
      showSearch
      placeholder="Brand"
      style={{ width: '100%' }}
      value={brandId}
      onChange={setBrandId}
      onSearch={setBrandSearch}
      filterOption={false}
      dropdownRender={(menu) => (
        <>
          {menu}
          <Divider style={{ margin: '8px 0' }} />
          <Space style={{ padding: '0 8px 4px' }}>
            <Button type="link" icon={<PlusOutlined />} onClick={() => { brandForm.resetFields(); setBrandModalOpen(true); }}>
              New brand
            </Button>
          </Space>
        </>
      )}
    >
      {brands.map(b => <Option key={b._id} value={b._id}>{b.name}</Option>)}
    </Select>
  );

  return (
    <div className="supplier-page">
      <div className="supplier-header">
        <Title level={3} style={{ margin: 0 }}>Supplier</Title>
        <Space>
          <Button type="primary" icon={<UploadOutlined />} onClick={() => { uploadForm.resetFields(); setFileList([]); setUploadOpen(true); }}>
            Upload
          </Button>
        </Space>
      </div>

      <Card className="supplier-filters">
        <Row gutter={[12, 12]}>
          <Col xs={24} md={8}>
            <Input
              placeholder="Search by item, tag, category or brand"
              value={q}
              onChange={(e) => { setQ(e.target.value); setPage(1); }}
              allowClear
            />
          </Col>
          <Col xs={24} md={8}>{BrandSelect}</Col>
          <Col xs={24} md={8}>
            <Space style={{ width: '100%' }} direction="vertical">
              <Select
                mode="tags"
                style={{ width: '100%' }}
                value={tags}
                onChange={(v) => { setTags(v); setPage(1); }}
                onSearch={(text) => debouncedFetchTags(text)}
                filterOption={false}
                placeholder="Tags (โต๊ะ, เก้าอี้, ...)"
                tokenSeparators={[',']}
                options={tagOptions.map(t => ({ value: t, label: t }))}
              />
              <Select
                mode="tags"
                style={{ width: '100%' }}
                value={cats}
                onChange={(v) => { setCats(v); setPage(1); }}
                onSearch={(text) => debouncedFetchCats(text)}
                filterOption={false}
                placeholder="Categories (Brand lines, types, ...)"
                tokenSeparators={[',']}
                options={catOptions.map(c => ({ value: c, label: c }))}
              />
            </Space>
          </Col>
        </Row>
      </Card>

      <div className="supplier-grid">
        <Row gutter={[16, 16]}>
          {items.map(it => (
            <Col key={it._id} xs={24} sm={12} md={8} lg={6} xl={6} xxl={4}>
              <Card
                loading={loading}
                hoverable
                cover={<SupplierCover it={it} onClick={() => openDetail(it)} />}
                actions={[
                  <Tooltip title="View details" key="view">
                    <EyeOutlined onClick={() => openDetail(it)} />
                  </Tooltip>,
                  <Tooltip title="Download" key="dl">
                    <a
                      href={`${API_BASE_URL}/api/suppliers/file/${it._id}`}
                      target="_blank"
                      rel="noreferrer"
                    >
                      <DownloadOutlined />
                    </a>
                  </Tooltip>,
                  <Tooltip title="Edit meta" key="edit">
                    <EditOutlined onClick={() => openEdit(it)} />
                  </Tooltip>,
                  <Popconfirm title="Delete this item?" onConfirm={() => deleteItem(it)} okText="Yes" cancelText="No" key="del">
                    <DeleteOutlined style={{ color: '#f5222d' }} />
                  </Popconfirm>
                ]}
              >
                <Card.Meta
                  title={<div className="supplier-card-title" title={it.name}>{it.name}</div>}
                  description={
                    <>
                      <div className="supplier-card-brand">{it.brandName}</div>
                      <div className="supplier-card-tags">
                        {(it.tags || []).slice(0, 4).map(t => <Tag key={t}>{t}</Tag>)}
                        {(it.categories || []).slice(0, 2).map(c => <Tag color="blue" key={c}>{c}</Tag>)}
                      </div>
                    </>
                  }
                />
              </Card>
            </Col>
          ))}
        </Row>
      </div>

      <div style={{ margin: '16px 0', textAlign: 'right' }}>
        <Pagination
          current={page}
          total={total}
          pageSize={limit}
          showSizeChanger
          pageSizeOptions={['12','24','48','96']}
          onChange={(p, ps) => { setPage(p); setLimit(ps); }}
        />
      </div>

      {/* Create Brand Modal */}
      <Modal
        open={brandModalOpen}
        onCancel={() => setBrandModalOpen(false)}
        onOk={createBrand}
        okText="Create Brand"
        title="New Brand"
        destroyOnClose
      >
        <Form form={brandForm} layout="vertical">
          <Form.Item name="name" label="Brand Name" rules={[{ required: true, message: 'Brand name is required' }]}>
            <Input placeholder="Brand (e.g., IKEA)" />
          </Form.Item>
          <Form.Item name="description" label="Description">
            <Input.TextArea rows={3} placeholder="Optional" />
          </Form.Item>
        </Form>
      </Modal>

      {/* Upload Modal */}
      <Modal
        open={uploadOpen}
        onCancel={() => { setUploadOpen(false); setFileList([]); }}
        footer={null}
        title="Upload Item (multiple images, or a document)"
        destroyOnClose
      >
        <Form form={uploadForm} layout="vertical" initialValues={{ brandId }}>
          <Form.Item label="Brand" required>
            <Select
              placeholder="Select existing brand (or create new below)"
              value={uploadForm.getFieldValue('brandId')}
              onChange={(v) => {
                uploadForm.setFieldsValue({ brandId: v, brandName: undefined });
              }}
              showSearch
              filterOption={(input, opt) => (opt?.children ?? '').toLowerCase().includes(input.toLowerCase())}
              allowClear
              dropdownRender={(menu) => (
                <>
                  {menu}
                  <Divider style={{ margin: '8px 0' }} />
                  <div style={{ padding: '8px' }}>
                    <Text type="secondary">New brand:</Text>
                    <Space.Compact style={{ width: '100%', marginTop: 6 }}>
                      <Input
                        placeholder="Type new brand name"
                        onChange={(e) => uploadForm.setFieldsValue({ brandName: e.target.value })}
                      />
                      <Button
                        type="primary"
                        onClick={async () => {
                          const name = (uploadForm.getFieldValue('brandName') || '').trim();
                          if (!name) return;
                          try {
                            const res = await axios.post(`${API_BASE_URL}/api/suppliers/brands`, { name }, { withCredentials: true });
                            await axios
                              .get(`${API_BASE_URL}/api/suppliers/brands?search=`, { withCredentials: true })
                              .then(r => setBrands(r.data || []))
                              .catch(() => {});
                            uploadForm.setFieldsValue({ brandId: res.data?._id, brandName: undefined });
                          } catch (e) {
                            Modal.error({ title: 'Create Brand Failed', content: e?.response?.data?.error || 'Unknown error' });
                          }
                        }}
                      >
                        Add
                      </Button>
                    </Space.Compact>
                  </div>
                </>
              )}
            >
              {brands.map(b => <Option key={b._id} value={b._id}>{b.name}</Option>)}
            </Select>
            <Form.Item name="brandId" noStyle><input type="hidden" /></Form.Item>
            <Form.Item name="brandName" noStyle><input type="hidden" /></Form.Item>
          </Form.Item>

          <Form.Item name="name" label="Item Name">
            <Input placeholder="Optional (defaults to filename)" />
          </Form.Item>

          <Form.Item name="tags" label="Tags">
            <Select
              mode="tags"
              tokenSeparators={[',']}
              placeholder="โต๊ะ, เก้าอี้, ... (hit Enter to add)"
              options={tagOptions.map(t => ({ value: t, label: t }))}
              onSearch={(text) => debouncedFetchTags(text)}
              filterOption={false}
            />
          </Form.Item>

          <Form.Item name="categories" label="Categories">
            <Select
              mode="tags"
              tokenSeparators={[',']}
              placeholder="Any categories"
              options={catOptions.map(c => ({ value: c, label: c }))}
              onSearch={(text) => debouncedFetchCats(text)}
              filterOption={false}
            />
          </Form.Item>

          <Form.Item label="Files" extra="Add as many images as you like (thumbnail shows the first 3), or a single document.">
            <Upload
              accept=".png,.jpg,.jpeg,.gif,.webp,.pdf,.doc,.docx,.xls,.xlsx,.txt"
              multiple
              listType="picture"
              fileList={fileList}
              beforeUpload={(file) => {
                if (beforeUpload(file) === Upload.LIST_IGNORE) return Upload.LIST_IGNORE;
                return false; // prevent auto-upload; we submit manually
              }}
              onChange={({ fileList: fl }) => setFileList(fl)}
              onRemove={(file) => setFileList(prev => prev.filter(f => f.uid !== file.uid))}
            >
              <Button icon={<UploadOutlined />}>Select files</Button>
            </Upload>
          </Form.Item>

          <div style={{ textAlign: 'right' }}>
            <Space>
              <Button onClick={() => { setUploadOpen(false); }}>Cancel</Button>
              <Button type="primary" loading={uploading} onClick={doUpload}>
                Upload
              </Button>
            </Space>
          </div>
        </Form>
      </Modal>

      {/* Edit Modal */}
      <Modal
        open={editOpen}
        onCancel={() => setEditOpen(false)}
        onOk={saveEdit}
        okText="Save"
        confirmLoading={savingEdit}
        width={editImageCount > 0 ? 640 : 520}
        title={`Edit: ${editingItem?.name || ''}`}
        destroyOnClose
      >
        <Form form={editForm} layout="vertical">
          <Form.Item name="name" label="Item Name" rules={[{ required: true, message: 'Item name is required' }]}>
            <Input />
          </Form.Item>
          <Form.Item name="tags" label="Tags">
            <Select
              mode="tags"
              tokenSeparators={[',']}
              options={tagOptions.map(t => ({ value: t, label: t }))}
              onSearch={(text) => debouncedFetchTags(text)}
              filterOption={false}
            />
          </Form.Item>
          <Form.Item name="categories" label="Categories">
            <Select
              mode="tags"
              tokenSeparators={[',']}
              options={catOptions.map(c => ({ value: c, label: c }))}
              onSearch={(text) => debouncedFetchCats(text)}
              filterOption={false}
            />
          </Form.Item>

          {editImageCount > 0 && editingItem && (
            <Form.Item label="Images">
              <div className="supplier-edit-images">
                {Array.from({ length: editImageCount }).map((_, i) => {
                  const marked = editRemove.includes(i);
                  return (
                    <div className={`supplier-edit-thumb${marked ? ' is-removed' : ''}`} key={i}>
                      <img
                        alt={`${editingItem.name} ${i + 1}`}
                        src={imageUrl(editingItem._id, i, true, editingItem.updatedAt)}
                      />
                      <Tooltip title={marked ? 'Undo remove' : 'Remove image'}>
                        <Button
                          size="small"
                          shape="circle"
                          danger={!marked}
                          className="supplier-edit-thumb-btn"
                          icon={<DeleteOutlined />}
                          onClick={() => toggleRemove(i)}
                        />
                      </Tooltip>
                    </div>
                  );
                })}
              </div>
              {editRemove.length > 0 && (
                <Text type="danger" style={{ display: 'block', marginTop: 6 }}>
                  {editRemove.length} image(s) will be removed on save.
                </Text>
              )}

              <Upload
                accept=".png,.jpg,.jpeg,.gif,.webp"
                multiple
                listType="picture"
                fileList={editNewFiles}
                beforeUpload={(file) => {
                  if (!isImg(file.type)) {
                    Modal.error({ title: 'Images only', content: 'Only images can be added here.' });
                    return Upload.LIST_IGNORE;
                  }
                  return false; // submit manually on save
                }}
                onChange={({ fileList: fl }) => setEditNewFiles(fl)}
                onRemove={(file) => setEditNewFiles(prev => prev.filter(f => f.uid !== file.uid))}
                style={{ marginTop: 8 }}
              >
                <Button icon={<UploadOutlined />} style={{ marginTop: 8 }}>Add images</Button>
              </Upload>
            </Form.Item>
          )}
        </Form>
      </Modal>

      {/* Detail Modal */}
      <Modal
        open={detailOpen}
        onCancel={() => { setDetailOpen(false); setDetailItem(null); }}
        footer={null}
        width={760}
        title={detailItem?.name}
        destroyOnClose
      >
        {detailItem && (
          <div className="supplier-detail">
            {imageCountOf(detailItem) > 0 ? (
              <div className="supplier-detail-gallery">
                <Image.PreviewGroup>
                  {Array.from({ length: imageCountOf(detailItem) }).map((_, i) => (
                    <Image
                      key={i}
                      src={imageUrl(detailItem._id, i, true, detailItem.updatedAt)}
                      alt={`${detailItem.name} #${i + 1}`}
                    />
                  ))}
                </Image.PreviewGroup>
              </div>
            ) : (
              <div className="supplier-card-doc">
                <FileIcon mime={detailItem.mimeType} />
                <a
                  href={`${API_BASE_URL}/api/suppliers/file/${detailItem._id}`}
                  target="_blank"
                  rel="noreferrer"
                >
                  <DownloadOutlined /> {detailItem.originalName || detailItem.name}
                </a>
              </div>
            )}

            <Descriptions size="small" column={1} bordered style={{ marginTop: 16 }}>
              <Descriptions.Item label="Brand">{detailItem.brandName || '-'}</Descriptions.Item>
              <Descriptions.Item label="Images">{imageCountOf(detailItem)}</Descriptions.Item>
              <Descriptions.Item label="Tags">
                {(detailItem.tags || []).length
                  ? (detailItem.tags || []).map(t => <Tag key={t}>{t}</Tag>)
                  : '-'}
              </Descriptions.Item>
              <Descriptions.Item label="Categories">
                {(detailItem.categories || []).length
                  ? (detailItem.categories || []).map(c => <Tag color="blue" key={c}>{c}</Tag>)
                  : '-'}
              </Descriptions.Item>
              <Descriptions.Item label="Created by">
                {detailItem.createdBy
                  ? `${detailItem.createdBy.firstName || ''} ${detailItem.createdBy.lastName || ''} ${detailItem.createdBy.email ? `(${detailItem.createdBy.email})` : ''}`.trim() || '-'
                  : '-'}
              </Descriptions.Item>
            </Descriptions>

            <div style={{ textAlign: 'right', marginTop: 16 }}>
              <Space>
                <Button icon={<EditOutlined />} onClick={() => { setDetailOpen(false); openEdit(detailItem); }}>
                  Edit meta
                </Button>
                <a
                  href={`${API_BASE_URL}/api/suppliers/file/${detailItem._id}`}
                  target="_blank"
                  rel="noreferrer"
                >
                  <Button icon={<DownloadOutlined />}>Download</Button>
                </a>
              </Space>
            </div>
          </div>
        )}
      </Modal>
    </div>
  );
};

function FileIcon({ mime }) {
  const label = (mime || '').split('/').pop() || 'file';
  return <div className="file-icon">{label.toUpperCase()}</div>;
}

// Card cover: shows up to 3 image thumbnails in a grid, or a doc icon.
function SupplierCover({ it, onClick }) {
  const count = imageCountOf(it);
  if (count > 0) {
    const n = Math.min(count, 3);
    return (
      <div className={`supplier-cover supplier-cover-${n}`} onClick={onClick}>
        {Array.from({ length: n }).map((_, i) => (
          <div className="supplier-cover-cell" key={i}>
            <img alt={`${it.name} ${i + 1}`} src={imageUrl(it._id, i, true, it.updatedAt)} />
          </div>
        ))}
      </div>
    );
  }
  return (
    <div className="supplier-card-doc" onClick={onClick}>
      <FileIcon mime={it.mimeType} />
      <Text type="secondary">{it.originalName || it.name}</Text>
    </div>
  );
}

export default Supplier;
